import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Activity, Palette, Users, Calendar, Flame, ArrowRight, Sparkles } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { CommunityCard, ChallengeCard, ProjectCard } from "@/components/cards";
import { Loader, SectionHeader } from "@/components/ui-bits";

const ICON = { photography: Camera, "fitness-wellness": Activity, "art-creative": Palette };
const FUTURE = ["Gaming", "Gardening", "DIY", "Dance", "Crafts", "Fashion", "Books", "Travel", "Technology", "Collecting", "Sports", "Languages"];

export default function Explore() {
  const [passions, setPassions] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/passions").then((r) => setPassions(r.data));
    api.get("/communities?sort=popular").then((r) => setCommunities(r.data));
    api.get("/challenges").then((r) => setChallenges(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  if (!passions) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-12">
      <section className="pa-in">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#55708F] mb-2">Explore</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Discover a passion to dive into</h1>
        <p className="text-[#071126]/60 mt-2 text-lg max-w-2xl">Find your thing, then find your people. Each passion is a whole world of communities, events and creations.</p>
      </section>

      {/* Passion tiles */}
      <section>
        <div className="grid gap-6 md:grid-cols-3">
          {passions.map((p) => {
            const Icon = ICON[p.slug] || Sparkles;
            return (
              <Link key={p.slug} to={`/passions/${p.slug}`} data-testid={`passion-tile-${p.slug}`} className="pa-card pa-card-hover overflow-hidden group flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  <img src={fileUrl(p.cover)} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/85 via-[#071126]/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-white">
                    <span className="h-9 w-9 rounded-xl bg-white/15 backdrop-blur grid place-items-center"><Icon className="h-4.5 w-4.5" /></span>
                    <div><p className="text-[10px] uppercase tracking-wider opacity-80">{p.category}</p><h3 className="font-bold text-lg leading-tight">{p.title}</h3></div>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-sm text-[#071126]/60 line-clamp-2 flex-1">{p.description}</p>
                  <div className="flex items-center gap-4 mt-4 text-xs font-medium text-[#55708F]">
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{p.community_count} communities</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{p.event_count}</span>
                    <span className="inline-flex items-center gap-1"><Flame className="h-3.5 w-3.5" />{p.challenge_count}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        <div className="mt-6 pa-card p-5 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[#071126] mr-1">More passions coming soon:</span>
          {FUTURE.map((f) => <span key={f} className="px-3 py-1 rounded-full bg-[#071126]/5 text-xs text-[#071126]/55 font-medium">{f}</span>)}
        </div>
      </section>

      <section>
        <SectionHeader eyebrow="Trending" title="Popular communities"
          action={<Link to="/communities" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1">All<ArrowRight className="h-4 w-4" /></Link>} />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{communities.slice(0, 6).map((c) => <CommunityCard key={c.id} c={c} />)}</div>
      </section>

      <section>
        <SectionHeader eyebrow="Get moving" title="Active challenges" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{challenges.slice(0, 3).map((c) => <ChallengeCard key={c.id} c={c} />)}</div>
      </section>

      <section>
        <SectionHeader eyebrow="Showcase" title="Fresh creations from the community" />
        <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{projects.slice(0, 8).map((p) => <ProjectCard key={p.id} p={p} />)}</div>
      </section>
    </div>
  );
}
